import React from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {TItemId} from 'src/constants';
import {TUserConfig, useGetUserConfigQuery, useSetUserConfigMutation} from 'src/containers/User';

import {EBannerCategory, TBannerCategory} from './definitions';
import {closeBanner, selectIsBannerOpen} from './slice';

type TConfig = {category: TBannerCategory; id?: TItemId};

const bannerCategoryToUserConfig: Partial<Record<TBannerCategory, keyof TUserConfig>> = {
  [EBannerCategory.NEWS]: 'news',
  [EBannerCategory.ADVICE]: 'advices',
  [EBannerCategory.ONBOARDING_TUTORIAL]: 'onboardingTutorial',
};

export const useBannerConfig = ({id, category}: TConfig) => {
  const dispatch = useDispatch();
  const userConfigField = bannerCategoryToUserConfig[category];

  const {data: config, isLoading} = useGetUserConfigQuery(undefined, {skip: !id});
  const [setUserConfig] = useSetUserConfigMutation();

  const isBannerOpen = useSelector(selectIsBannerOpen({id, category}));

  const handleClose = React.useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(closeBanner({id, category}));
  }, [closeBanner, id]);

  const handleRemove = React.useCallback(() => {
    if (!id) {
      return;
    }

    if (!userConfigField) {
      throw new Error(
        `Can't apply remove action for banner category "${category}". Add category support to user config api`,
      );
    }

    if (!config) {
      return setUserConfig({
        [userConfigField]: {[id]: {isRemoved: true}},
      });
    }

    const allByCategory = config[userConfigField] ?? {};

    setUserConfig({
      ...config,
      [userConfigField]: {...allByCategory, [id]: {isRemoved: true}},
    });
  }, [config, category, id, userConfigField]);

  const isBannerRemoved = React.useMemo(() => {
    if (isLoading) {
      return true;
    }

    if (!userConfigField || !id || !config) {
      return false;
    }

    return !!config?.[userConfigField]?.[id]?.isRemoved;
  }, [isLoading, userConfigField, id, config]);

  return {
    removeBanner: handleRemove,
    closeBanner: handleClose,
    isBannerOpen,
    isBannerRemoved,
  };
};
